import axios from 'axios';
import { useAuthStore } from '@/stores/authStore.js';

// Toggle this to enable/disable fake delay globally
let enableFakeDelay = false;
// You can also set the delay duration here (in ms)
let fakeDelayDuration = 1500;

function setFakeDelay(enabled, duration = 1500) {
  enableFakeDelay = enabled;
  fakeDelayDuration = duration;
}

function fakeDelay() {
  if (!enableFakeDelay) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, fakeDelayDuration));
}

export default function useAxiosClient(baseURL = null) {
  const authStore = useAuthStore();

  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      const token = authStore.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        const status = error.response.status;

        // Handle 401 Unauthorized - token invalid or expired
        if (status === 401) {
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            authStore.logout();
          }
        }

        // Handle 403 Forbidden - insufficient permissions or deactivated user
        if (status === 403) {
          const message = error.response.data?.message || '';
          const isDeactivatedError = message.toLowerCase().includes('deactivated');
          const isPlanRestricted = error.response.data?.code === 'PLAN_RESTRICTED';
          const requiresSubscription = error.response.data?.code === 'SUBSCRIPTION_REQUIRED';

          if (isPlanRestricted) {
            // Plan does not include this feature — not an auth failure, let the
            // caller handle it without logging the user out.
          } else if (requiresSubscription) {
            // Registered but no confirmed payment — keep auth token and let the
            // payment modal handle recovery instead of logging out.
            authStore.requiresSubscription = true;
          } else if (isDeactivatedError) {
            // Keep auth token — modal handles recovery
            authStore.isDeactivated = true;
          } else if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            // Existing behaviour: permission denied, log out
            authStore.setAuthentication(null);
            authStore.setUser(null);
            authStore.isAuthenticated = false;
            window.location.href = '/login';
          }
        }
      }
      return Promise.reject(error);
    }
  );

  const _handleResponse = async (fn) => {
    const response = await fn;
    if (enableFakeDelay) {
      await fakeDelay();
    }
    return response.data;
  };

  const get = (path, params = {}, options = {}) => _handleResponse(instance.get(path, { params, ...options }));

  const post = (path, data = {}, options = {}) => _handleResponse(instance.post(path, data, options));

  const patch = (path, data = {}, options = {}) => _handleResponse(instance.patch(path, data, options));

  const put = (path, data = {}, options = {}) => _handleResponse(instance.put(path, data, options));

  const deleteMethod = (path, options = {}) => _handleResponse(instance.delete(path, options));

  return {
    get,
    post,
    patch,
    put,
    delete: deleteMethod,
    instance,
    setFakeDelay // Expose the setter so consumers can toggle delay
  };
}
